/**
 * AI Colleagues Context
 *
 * Provides global access to open the AI Colleagues modal from anywhere in the app.
 * Similar to CommandPaletteContext but for the AI assistant feature.
 */
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import type { ColleagueId, AIColleagueContext as AIContext } from '@/hooks/ai-colleagues';

interface AIColleaguesState {
  /** Whether the modal is open */
  open: boolean;
  /** Pre-selected colleague ID */
  colleagueId?: ColleagueId;
  /** Context to pass to the colleague */
  context?: AIContext;
  /** Initial message to send immediately when modal opens */
  initialMessage?: string;
}

interface OpenAIColleaguesOptions {
  colleagueId?: ColleagueId;
  context?: AIContext;
  /** Pre-fill and optionally auto-send a message */
  initialMessage?: string;
}

interface AIColleaguesContextValue {
  /** Current state */
  state: AIColleaguesState;
  /** Open the AI Colleagues modal */
  openAIColleagues: (options?: OpenAIColleaguesOptions) => void;
  /** Close the modal */
  closeAIColleagues: () => void;
  /** Toggle the modal */
  toggleAIColleagues: () => void;
  /** Clear the initial message (call after consuming it) */
  clearInitialMessage: () => void;
}

const AIColleaguesContext = createContext<AIColleaguesContextValue | undefined>(undefined);

/**
 * AI Colleagues Provider
 * Wrap your app with this to enable global access to the AI modal.
 */
export const AIColleaguesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AIColleaguesState>({
    open: false
  });

  const openAIColleagues = useCallback(
    (options?: OpenAIColleaguesOptions) => {
      setState({
        open: true,
        colleagueId: options?.colleagueId,
        context: options?.context,
        initialMessage: options?.initialMessage
      });
    },
    []
  );

  const closeAIColleagues = useCallback(() => {
    setState((prev) => ({
      ...prev,
      open: false
    }));
    // Clear options after animation
    setTimeout(() => {
      setState({ open: false });
    }, 300);
  }, []);

  const toggleAIColleagues = useCallback(() => {
    setState((prev) => ({
      ...prev,
      open: !prev.open
    }));
  }, []);

  const clearInitialMessage = useCallback(() => {
    setState((prev) => ({
      ...prev,
      initialMessage: undefined
    }));
  }, []);

  // Global keyboard shortcut: Cmd+J / Ctrl+J
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'j') {
        e.preventDefault();
        toggleAIColleagues();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [toggleAIColleagues]);

  return (
    <AIColleaguesContext.Provider
      value={{
        state,
        openAIColleagues,
        closeAIColleagues,
        toggleAIColleagues,
        clearInitialMessage
      }}
    >
      {children}
    </AIColleaguesContext.Provider>
  );
};

/**
 * Hook to access AI Colleagues context
 */
export function useAIColleaguesContext(): AIColleaguesContextValue {
  const context = useContext(AIColleaguesContext);
  if (!context) {
    throw new Error('useAIColleaguesContext must be used within AIColleaguesProvider');
  }
  return context;
}

/**
 * Convenience hook for simple open/close
 */
export function useAIColleaguesModal() {
  const { state, openAIColleagues, closeAIColleagues, clearInitialMessage } = useAIColleaguesContext();
  return {
    open: state.open,
    colleagueId: state.colleagueId,
    context: state.context,
    initialMessage: state.initialMessage,
    openAIColleagues,
    closeAIColleagues,
    clearInitialMessage
  };
}
